import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TABLE_SX, tableContainerSx } from "../DailyExpense/tableStyles";
import FigureCells from "./FigureCells";
import ReportTableRow from "./ReportTableRow";
import { ESTIMATE_BADGE_TEXT } from "./constants";
import { ReportLine, ReportView, Thresholds, tonesOf } from "./incomeExpenseReport";
import {
  FIRST_HEAD_ROW_SX,
  NAME_HEAD_SX,
  NUMBER_HEAD_SX,
  SECOND_HEAD_ROW_SX,
  TABLE_MAX_HEIGHT,
  TOTAL_LABEL_SX,
  TOTAL_ROW_SX,
} from "./reportTableStyles";

const SPAN_TWO_ROWS = 2;
const NAME_HEAD_TEXT = { branch: "สาขา", route: "สาย", day: "สาย" } as const;

interface IncomeExpenseTableProps {
  view: ReportView;
  thresholds: Thresholds | null;
  isEstimate: boolean;
  showsMissingDocument: boolean;
  onDrill: (line: ReportLine) => void;
  onOpenDay: (line: ReportLine) => void;
}

const totalLabelOf = (view: ReportView): string =>
  view.level === "route" ? `รวมทั้งสิ้น (${view.lines.length} สาย)` : "รวมทั้งสิ้น";

/** An expense header that carries the "ประมาณการ" badge in estimate mode. */
function ExpenseHead({ title, isEstimate }: Readonly<{ title: string; isEstimate: boolean }>) {
  return (
    <TableCell>
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
        {title}
        {isEstimate && <Chip size="small" color="primary" variant="outlined" label={ESTIMATE_BADGE_TEXT} />}
      </Stack>
    </TableCell>
  );
}

/**
 * รับจ่าย: every branch, the routes of a branch, or the days of a route, with the totals row.
 * Header, No + name and the totals row stay pinned; figures are right-aligned in tabular digits.
 */
export default function IncomeExpenseTable({
  view,
  thresholds,
  isEstimate,
  showsMissingDocument,
  onDrill,
  onOpenDay,
}: Readonly<IncomeExpenseTableProps>) {
  const isDayLevel = view.level === "day";
  const totalDayText = isDayLevel ? `${view.totalWorkDayCount} วัน` : view.totalWorkDayCount;

  return (
    <TableContainer sx={tableContainerSx(TABLE_MAX_HEIGHT)}>
      <Table size="small" stickyHeader sx={TABLE_SX}>
        <TableHead>
          <TableRow sx={FIRST_HEAD_ROW_SX}>
            <TableCell rowSpan={SPAN_TWO_ROWS} sx={NUMBER_HEAD_SX}>
              No
            </TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS} sx={NAME_HEAD_SX}>
              {NAME_HEAD_TEXT[view.level]}
            </TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>{isDayLevel ? "วันที่" : "วันทำงาน"}</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>เป้าหมาย</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>ยอดขาย</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>ผล</TableCell>
            <TableCell colSpan={4}>ต้นทุน</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>กำไรขั้นต้น</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>%</TableCell>
            <TableCell colSpan={3}>ค่าใช้จ่าย</TableCell>
            <TableCell rowSpan={SPAN_TWO_ROWS}>%</TableCell>
            <TableCell colSpan={2}>กำไร (ขาดทุน)</TableCell>
            <TableCell>ค่าน้ำมัน</TableCell>
            <TableCell>บิล</TableCell>
          </TableRow>
          <TableRow sx={SECOND_HEAD_ROW_SX}>
            <TableCell>ขาย</TableCell>
            <TableCell>เสีย</TableCell>
            <TableCell>%</TableCell>
            <TableCell>รวม</TableCell>
            <ExpenseHead title="ฝ่ายขาย" isEstimate={isEstimate} />
            <ExpenseHead title="บริหาร" isEstimate={isEstimate} />
            <TableCell>รวม</TableCell>
            <TableCell>บาท</TableCell>
            <TableCell>%</TableCell>
            <TableCell>% ยอดขาย</TableCell>
            <TableCell>เฉลี่ย</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {view.lines.map((line, index) => (
            <ReportTableRow
              key={line.key}
              index={index}
              line={line}
              thresholds={thresholds}
              isEstimate={isEstimate}
              showsMissingDocument={showsMissingDocument}
              onDrill={isDayLevel ? undefined : onDrill}
              onOpen={isDayLevel ? onOpenDay : undefined}
            />
          ))}
          <TableRow sx={TOTAL_ROW_SX}>
            <TableCell colSpan={SPAN_TWO_ROWS} sx={TOTAL_LABEL_SX}>
              {totalLabelOf(view)}
            </TableCell>
            <TableCell align="right">{totalDayText}</TableCell>
            <FigureCells
              figures={view.totals}
              tones={tonesOf(view.totals, thresholds)}
              isEstimate={isEstimate}
              showsMissingDocument={showsMissingDocument}
            />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
